// pattern-overlap: two pattern files whose line-set Jaccard exceeds the threshold
// are merge candidates. Deterministic; no model.

import type { ParsedFile, Suggestion, SuggestContext, SuggestionProvider } from "../../types.js";

/** Normalised, content-bearing lines of a file (lowercased, trimmed, deduped). */
function lineSet(file: ParsedFile): Set<string> {
  const set = new Set<string>();
  for (const raw of file.lines) {
    const line = raw.trim().toLowerCase();
    // Skip blanks, headings, and list/table scaffolding that match trivially.
    if (!line || /^[#>|`*-]+$/.test(line)) continue;
    set.add(line);
  }
  return set;
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const x of a) if (b.has(x)) intersection++;
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

function isPatternFile(source: string): boolean {
  const base = source.split("/").pop() ?? source;
  return source.includes("patterns/") && base !== "INDEX.md" && base !== "README.md";
}

export function findOverlaps(ctx: SuggestContext): Suggestion[] {
  const patterns = ctx.files
    .filter((f) => isPatternFile(f.source))
    .map((f) => ({ source: f.source, set: lineSet(f) }));

  const suggestions: Suggestion[] = [];
  for (let i = 0; i < patterns.length; i++) {
    for (let j = i + 1; j < patterns.length; j++) {
      const score = jaccard(patterns[i].set, patterns[j].set);
      if (score < ctx.settings.overlapThreshold) continue;
      const shared = sharedLineCount(patterns[i].set, patterns[j].set);
      suggestions.push({
        kind: "pattern-overlap",
        title: `Merge overlapping patterns: ${patterns[i].source} + ${patterns[j].source}`,
        rationale: `These two pattern files share ${Math.round(score * 100)}% of their content (line-set Jaccard). Merging removes duplicated guidance the agent must reconcile.`,
        estimatedSaving: shared * 8, // ~8 tokens/line saved by removing one copy
        files: [patterns[i].source, patterns[j].source],
      });
    }
  }
  return suggestions;
}

function sharedLineCount(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const x of a) if (b.has(x)) n++;
  return n;
}

export const patternOverlapProvider: SuggestionProvider = {
  name: "pattern-overlap",
  run: findOverlaps,
};
